﻿/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useEffect, useState} from 'react'
import {GroupedPackageItem} from 'types/entities/GroupedPackageItem'
import {PackageItem} from 'types/entities/PackageItem'
import {ServerImage} from 'components/ServerImage'
import {currencyNumberFormat, getSelectedUpsell, translate} from 'utils/utility'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import BookingAction from 'store/booking/BookingAction'
import {useTranslation} from 'react-i18next'
import {toast} from 'utils/toastUtils'
import {useDebouncedCallback} from 'use-debounce'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import {closeSecondaryModal, openSecondaryModal} from 'helpers/actions'
import {ModalSize} from 'types/enums/ModalSize'
import clsx from 'clsx'
import UiRoundedContent from "components/Ui/UiRoundedContent";

interface AssignedUpsell {
    upsell_id: number
    assigned_count: number
}

// Sub-item row inside the slide-over
interface SlideOverSubItemProps {
    packageItem: PackageItem
    assignedUpsells: AssignedUpsell[]
    setAssignedUpsells: React.Dispatch<React.SetStateAction<AssignedUpsell[]>>
}

const SlideOverSubItem = (props: SlideOverSubItemProps) => {
    const currentCount =
        props.assignedUpsells.find((u) => u.upsell_id === props.packageItem.id)?.assigned_count ?? 0
    const [count, setCount] = useState(currentCount)
    const maximumValue = props.packageItem?.packageItemVariant?.maximum_count ?? 100


    const updateAssigned = (newCount: number) => {
        const upsellId = props.packageItem.id
        props.setAssignedUpsells((prev) => {
            const idx = prev.findIndex((u) => u.upsell_id === upsellId)
            if (idx > -1) {
                return prev.map((u) => (u.upsell_id === upsellId ? { ...u, assigned_count: newCount } : u))
            }
            return [...prev, { upsell_id: upsellId, assigned_count: newCount }]
        })
    }

    // Use local `count` (not stale `currentCount` from props) so rapid clicks are safe
    const handleStep = (delta: number) => {
        const next = Math.min(Math.max(count + delta, 0), maximumValue)
        setCount(next)
        updateAssigned(next)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const parsed = Number(e.target.value.trim())
        const safe = isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), maximumValue)
        setCount(safe)
        updateAssigned(safe)
    }


	useEffect(() => {
		setCount(currentCount)
	}, [props.assignedUpsells])

    return (
        <div className="flex items-center gap-3 py-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {translate(props.packageItem.name)}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                    {currencyNumberFormat(props.packageItem.price)}
                </p>
            </div>
            {/* Counter */}
            <div className="flex items-center gap-1 shrink-0">
                <button
                    className="size-9 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition text-lg font-bold active:scale-95"
                    type="button"
                    onClick={() => handleStep(-1)}
                >
                    −
                </button>
                <input
                    className="w-12 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-800 px-2 py-2 text-center text-sm font-bold text-slate-950 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    inputMode="numeric"
                    max={maximumValue}
                    min={0}
                    type="text"
                    value={count}
                    onChange={(e) => {
                        const val = Number(e.target.value)
                        if (isNaN(val)) return
                        const safe = Math.min(Math.max(val, 0), maximumValue)
                        setCount(safe)
                        updateAssigned(safe)
                    }}
                    onBlur={handleBlur}
                />
                <button
                    className="size-9 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition text-lg font-bold active:scale-95"
                    type="button"
                    onClick={() => handleStep(+1)}
                >
                    +
                </button>
            </div>
        </div>
    )
}

interface UpsellPriceListCombinationRowProps {
    item: GroupedPackageItem
    isCompact?: boolean
}

const toCustomTextValue = (value: unknown): string => {
    if (typeof value === 'string') {
        return value
    }

    if (typeof value === 'number') {
        return String(value)
    }

    return ''
}

const UpsellPriceListCombinationRow = (props: UpsellPriceListCombinationRowProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const bookingProcess = useTypedSelector((state) => state.bookingState.bookingProcess)
    const [isSlideOverOpen, setIsSlideOverOpen] = useState(false)

    useEffect(() => {
        if (!isSlideOverOpen) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsSlideOverOpen(false) }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [isSlideOverOpen])

    const isAnyUpsellSelected = props.item.items.some(
        (packageItem) => getSelectedUpsell(bookingProcess.upsell, packageItem) !== undefined
    )

    const [assignedUpsells, setAssignedUpsells] = useState<AssignedUpsell[]>([])
    const [showUpdateButton, setShowUpdateButton] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [customText, setCustomText] = useState('')

    const setDefaultValues = () => {
        const allPersonsCount = bookingProcess.filter.adults + bookingProcess.filter.children
        const nextAssigned = props.item.items.map((packageItem, index) => {
            const isCompetition = packageItem.extra?.groupName === 'Competitions'
            const defaultUpsellCount =
                packageItem.packageItemVariant?.minimum_count && packageItem.packageItemVariant?.maximum_count
                    ? allPersonsCount >= packageItem.packageItemVariant.minimum_count &&
                        allPersonsCount <= packageItem.packageItemVariant.maximum_count
                        ? allPersonsCount
                        : allPersonsCount < packageItem.packageItemVariant.minimum_count
                            ? packageItem.packageItemVariant.minimum_count
                            : packageItem.packageItemVariant.maximum_count
                    : 0
            const defaultCount = isCompetition ? 1 : index === 0 ? defaultUpsellCount : 0
            return { upsell_id: packageItem.id, assigned_count: defaultCount }
        })
        setAssignedUpsells(nextAssigned)
    }

    const presetFromStore = () => {
        const nextAssigned = props.item.items.map((packageItem) => {
            const selectedUpsell = getSelectedUpsell(bookingProcess.upsell, packageItem)
            return { upsell_id: packageItem.id, assigned_count: selectedUpsell ? selectedUpsell.count : 0 }
        })
        setAssignedUpsells(nextAssigned)
    }

    useEffect(() => {
        if (isAnyUpsellSelected) {
            presetFromStore()
        } else {
            setDefaultValues()
        }
    }, [])

    const computeHasDifferences = useCallback((): boolean => {
        for (const packageItem of props.item.items) {
            const assigned = assignedUpsells.find((u) => u.upsell_id === packageItem.id)?.assigned_count ?? 0
            const currentUpsell = getSelectedUpsell(bookingProcess.upsell, packageItem)
            const current = currentUpsell?.count ?? 0
            if (!currentUpsell && assigned === 0) continue
            if (currentUpsell && assigned === 0) return true
            if (assigned !== current) return true
        }
        return false
    }, [assignedUpsells, bookingProcess.upsell, props.item.items])

    const debouncedCheckDifferences = useDebouncedCallback(() => {
        setShowUpdateButton(computeHasDifferences())
    }, 300)

    useEffect(() => {
        if (!isAnyUpsellSelected) {
            setShowUpdateButton(false)
            debouncedCheckDifferences.cancel()
            return
        }
        debouncedCheckDifferences()
        return () => { debouncedCheckDifferences.cancel() }
    }, [isAnyUpsellSelected, computeHasDifferences, debouncedCheckDifferences])

    const validateCounts = () => {
        // Only validate if the variant explicitly declares min/max constraints
        if (!props.item.packageItemVariant) return true
        const total = assignedUpsells.reduce((sum, u) => sum + (u.assigned_count ?? 0), 0)
        const min = props.item.packageItemVariant.minimum_count ?? 0
        const max = props.item.packageItemVariant.maximum_count ?? Infinity
        if (min > 0 && total < min) {
            toast.error(t('cannot order less', { minimum_value: min }), { toastId: 'minimumDriverCount' })
            return false
        }
        if (max !== Infinity && total > max) {
            toast.error(t('We cannot take more people than: ') + max, { toastId: 'maximumDriverCount' })
            return false
        }
        return true
    }

    const onSetUpsell = () => {
        setIsLoading(true)
        if (!validateCounts()) {
            setIsLoading(false)
            return
        }
        assignedUpsells.forEach((upsell) => {
            const selectedPackage = props.item.items.find((item) => item.id === upsell.upsell_id)
            if (selectedPackage) {
                if (upsell.assigned_count > 0) {
                    dispatch(BookingAction.pushUpsell({ packageItem: selectedPackage, upsell_option: 0, count: upsell.assigned_count }))
                } else {
                    const currentUpsell = getSelectedUpsell(bookingProcess.upsell, selectedPackage)
                    if (currentUpsell) dispatch(BookingAction.removeUpsell(currentUpsell))
                }
            }
        })
        setIsLoading(false)
        toast.success(isAnyUpsellSelected ? t('Items updated') : t('Items added'))
        setIsSlideOverOpen(false)
    }

    const onRemoveUpsells = () => {
        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={t('Remove upsell')}
                    message={t('Are you sure you want to delete this upsell?')}
                    onConfirm={() => {
                        props.item.items.forEach((packageItem) => {
                            const currentUpsell = getSelectedUpsell(bookingProcess.upsell, packageItem)
                            if (currentUpsell) dispatch(BookingAction.removeUpsell(currentUpsell))
                        })
                        setDefaultValues()
                        toast.success(t('Upsells removed'))
                        dispatch(closeSecondaryModal())
                        setIsSlideOverOpen(false)
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    const displayImage = props.item.packageItemVariant?.image || props.item.image
    const displayName = props.item.packageItemVariant
        ? translate(props.item.packageItemVariant.name)
        : translate(props.item.name)
    const displayDescription = props.item.packageItemVariant
        ? translate(props.item.packageItemVariant.description_html || props.item.packageItemVariant.description)
        : translate(props.item.custom_text || props.item.description_html)
    const minCount = props.item.packageItemVariant?.minimum_count
    const maxCount = props.item.packageItemVariant?.maximum_count
    const totalAssigned = assignedUpsells.reduce((sum, u) => sum + u.assigned_count, 0)
    const inCartTotal = props.item.items.reduce((sum, packageItem) => {
        const selectedUpsell = getSelectedUpsell(bookingProcess.upsell, packageItem)
        return sum + (selectedUpsell?.count ?? 0)
    }, 0)
    const fromUnitPrice = props.item.items.length
        ? Math.min(...props.item.items.map((packageItem) => packageItem.price))
        : 0
    const totalDraftPrice = props.item.items.reduce((sum, packageItem) => {
        const assignedCount = assignedUpsells.find((u) => u.upsell_id === packageItem.id)?.assigned_count ?? 0
        return sum + packageItem.price * assignedCount
    }, 0)

    useEffect(() => {
        const normalizedCustomText = toCustomTextValue(customText)

        if (!isSlideOverOpen || normalizedCustomText.trim().length > 0) {
            return
        }

        setCustomText(toCustomTextValue(displayDescription || displayName))
    }, [customText, displayDescription, displayName, isSlideOverOpen])

    return (
        <>
            {/* Row – clickable */}
            <button
                className={clsx(
                    'UpsellPriceListCombinationRow group grid w-full grid-cols-1 gap-4 px-5 py-5 text-left transition hover:bg-blue-50/60 dark:hover:bg-blue-900/10 sm:grid-cols-[1fr_auto]',
                    isAnyUpsellSelected && 'bg-blue-50/40 dark:bg-blue-900/10'
                )}
                type="button"
                onClick={() => setIsSlideOverOpen(true)}
            >
                <div className="flex items-start gap-3 min-w-0">
                    {displayImage && (
                        <div className="shrink-0 size-12 overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800">
                            <ServerImage
                                alt={displayName}
                                className="w-full h-full object-cover"
                                src={displayImage}
                            />
                        </div>
                    )}
                    <div className="min-w-0">
                        <h3
                            className={clsx(
                                'font-semibold text-slate-950 dark:text-slate-100 group-hover:text-blue-700 dark:group-hover:text-blue-400 leading-snug',
                                props.isCompact ? 'text-sm' : 'text-base'
                            )}
                        >
                            {displayName}
                        </h3>
                        {displayDescription && (
                            <p
                                className="mt-1 text-sm leading-5 text-slate-500 dark:text-slate-400 line-clamp-2"
                                dangerouslySetInnerHTML={{ __html: displayDescription }}
                            />
                        )}
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            {props.item.items.length} {t('options')}
                            {(minCount || maxCount) && (
                                <span className="ml-2">
                                    {minCount && maxCount
                                        ? `· ${t('Min')} ${minCount} – ${t('Max')} ${maxCount}`
                                        : minCount
                                            ? `· ${t('Min')} ${minCount}`
                                            : `· ${t('Max')} ${maxCount}`}
                                </span>
                            )}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 sm:flex-col sm:items-end sm:justify-center shrink-0">
                    {isAnyUpsellSelected ? (
                        <>
                            <div className="rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white">
                                ✓ {inCartTotal}× {t('Selected')}
                            </div>
                            {showUpdateButton && (
                                <div className="rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white">
                                    {t('Update')}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1 text-xs font-semibold text-slate-700 dark:text-slate-300 group-hover:border-blue-400 group-hover:text-blue-600">
                            {t('Configure')}
                        </div>
                    )}
                </div>
            </button>

            {/* Bottom slide-over */}
            {isSlideOverOpen && (
                <div className="fixed inset-0 z-50">
                    <button
                        aria-label={t('Close')}
                        className="absolute inset-0 bg-slate-950/40 backdrop-blur-[2px]"
                        type="button"
                        onClick={() => setIsSlideOverOpen(false)}
                    />
                    <div className="absolute inset-x-0 bottom-0 mx-auto max-w-xl rounded-t-[2rem] bg-white dark:bg-gray-900 p-5 shadow-2xl ring-1 ring-slate-200 dark:ring-slate-700 animate-in slide-in-from-bottom duration-300 max-h-[90dvh] flex flex-col">
                        {/* Drag handle */}
                        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0" />

                        {/* Header */}
                        <div className="mb-4 flex items-start gap-3 shrink-0">
                            {displayImage && (
                                <div className="shrink-0 size-14 overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-800">
                                    <ServerImage
                                        alt={displayName}
                                        className="w-full h-full object-cover"
                                        src={displayImage}
                                    />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">
                                    {props.item.items[0]?.extra?.groupName}
                                </p>
                                <h2 className="mt-0.5 text-xl font-bold text-slate-950 dark:text-slate-100">
                                    {displayName}
                                </h2>
                                {(minCount || maxCount) && (
                                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                        {minCount && maxCount
                                            ? t('Min {{min}} – Max {{max}}', { min: minCount, max: maxCount })
                                            : minCount
                                                ? t('Min {{min}}', { min: minCount })
                                                : t('Max {{max}}', { max: maxCount })}
                                    </p>
                                )}
                            </div>
                            <button
                                className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                                type="button"
                                onClick={() => setIsSlideOverOpen(false)}
                            >
                                {t('Close')}
                            </button>
                        </div>

                        {/* Price summary */}
                        <div className="mb-4 flex items-center justify-between rounded-2xl bg-slate-50 dark:bg-slate-800 px-4 py-3 shrink-0">
                            <div>
                                <span className="text-sm text-slate-600 dark:text-slate-400">{t('Price per item')}</span>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    {t('In cart')}: {inCartTotal}
                                </p>
                            </div>
                            <div className="text-right">
                                <span className="text-lg font-bold text-slate-950 dark:text-slate-100 block">
                                    {currencyNumberFormat(fromUnitPrice)}+
                                </span>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {t('Total')}: {currencyNumberFormat(totalDraftPrice)}
                                </span>
                            </div>
                        </div>

                        {/* Item details */}
						<UiRoundedContent className={"mb-4 shrink-0"}>
                            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700">
                                <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                    {t('Item Details')}
                                </p>
                            </div>
                            <div className="p-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                <div className="sm:col-span-1">
                                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                        {t('Quantity')}
                                    </label>
                                    <input
                                        className="mt-2 w-full rounded-2xl border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base font-semibold text-slate-700 dark:text-slate-200"
                                        disabled
                                        type="text"
                                        value={String(totalAssigned)}
                                    />
                                </div>
                                <div className="sm:col-span-1">
                                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                        {t('Unit Price')}
                                    </label>
                                    <input
                                        className="mt-2 w-full rounded-2xl border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base font-semibold text-slate-700 dark:text-slate-200"
                                        disabled
                                        type="text"
                                        value={`${currencyNumberFormat(fromUnitPrice)}+`}
                                    />
                                </div>
                                <div className="sm:col-span-1">
                                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                        {t('Total Price')}
                                    </label>
                                    <input
                                        className="mt-2 w-full rounded-2xl border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-3 py-2 text-base font-semibold text-slate-700 dark:text-slate-200"
                                        disabled
                                        type="text"
                                        value={currencyNumberFormat(totalDraftPrice)}
                                    />
                                </div>
                            </div>
                        </UiRoundedContent>

                        {/* Custom text */}
                        <UiRoundedContent className="mb-4 shrink-0">
                            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                    {t('Custom Text')}
                                </p>
                                <button
                                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                    type="button"
                                    onClick={() =>
                                        setCustomText(
                                            toCustomTextValue(
                                                displayDescription || displayName
                                            )
                                        )
                                    }
                                >
                                    {t('Auto fill')}
                                </button>
                            </div>
                            <div className="p-3">
                                <textarea
                                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 shadow-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900"
                                    placeholder={t('Enter custom text...')}
                                    rows={3}
                                    value={customText}
                                    onChange={(e) => setCustomText(e.target.value)}
                                />
                            </div>
                        </UiRoundedContent>

                        {/* Sub-items */}
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {props.item.items.map((packageItem) => (
                                <SlideOverSubItem
                                    key={packageItem.id}
                                    assignedUpsells={assignedUpsells}
                                    packageItem={packageItem}
                                    setAssignedUpsells={setAssignedUpsells}
                                />
                            ))}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-4 shrink-0">
                            {isAnyUpsellSelected ? (
                                <>
                                    <button
                                        className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-blue-700 transition"
                                        disabled={isLoading}
                                        type="button"
                                        onClick={onSetUpsell}
                                    >
                                        {showUpdateButton ? t('Update') : t('Save Changes')}
                                    </button>
                                    <button
                                        className="rounded-2xl bg-red-50 dark:bg-red-900/30 px-5 py-3 text-sm font-bold text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition"
                                        type="button"
                                        onClick={onRemoveUpsells}
                                    >
                                        {t('Remove')}
                                    </button>
                                </>
                            ) : (
                                <button
                                    className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-blue-700 transition"
                                    disabled={isLoading}
                                    type="button"
                                    onClick={onSetUpsell}
                                >
                                    {t('Add')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default UpsellPriceListCombinationRow
